import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import {
  tableValue,
  moneyCell,
  percentCell
} from '../tableUtils'
import {
  buildFillRateExportFilename,
  resolveFillRateDifference
} from '../fillRateMetrics'
import {
  UI_COLOR_BORDER_NEUTRAL,
  UI_COLOR_BORDER_NEUTRAL_DARK,
  UI_COLOR_BORDER_NEUTRAL_MEDIUM,
  UI_COLOR_DANGER_BORDER,
  UI_COLOR_DANGER_SOFT,
  UI_COLOR_DANGER_TEXT,
  UI_COLOR_SUCCESS_BORDER,
  UI_COLOR_SUCCESS_SOFT,
  UI_COLOR_SUCCESS_TEXT,
  UI_COLOR_SURFACE_TABLE_HEADER,
  UI_COLOR_SURFACE_TABLE_STRIPE,
  UI_COLOR_TEXT_DARK,
  UI_COLOR_TEXT_GRAY,
  UI_COLOR_TEXT_NAVY,
  UI_COLOR_WHITE
} from '../../../../constants/uiColors'

import cookie from 'react-cookies'

const hexToRgb = (hexColor) => {
  const normalizedHex = hexColor.replace('#', '')
  return [
    parseInt(normalizedHex.slice(0, 2), 16),
    parseInt(normalizedHex.slice(2, 4), 16),
    parseInt(normalizedHex.slice(4, 6), 16)
  ]
}

const COLORS = {
  primary: hexToRgb(UI_COLOR_SURFACE_TABLE_HEADER),
  gray: hexToRgb(UI_COLOR_TEXT_GRAY),
  dark: hexToRgb(UI_COLOR_TEXT_DARK),
  navy: hexToRgb(UI_COLOR_TEXT_NAVY),
  lightBorder: hexToRgb(UI_COLOR_BORDER_NEUTRAL),
  lineBorder: hexToRgb(UI_COLOR_BORDER_NEUTRAL_MEDIUM),
  rowAlt: hexToRgb(UI_COLOR_SURFACE_TABLE_STRIPE),
  totalBorder: hexToRgb(UI_COLOR_BORDER_NEUTRAL_DARK),
  danger: hexToRgb(UI_COLOR_DANGER_TEXT),
  dangerBorder: hexToRgb(UI_COLOR_DANGER_BORDER),
  dangerBg: hexToRgb(UI_COLOR_DANGER_SOFT),
  success: hexToRgb(UI_COLOR_SUCCESS_TEXT),
  successBorder: hexToRgb(UI_COLOR_SUCCESS_BORDER),
  successBg: hexToRgb(UI_COLOR_SUCCESS_SOFT),
  white: hexToRgb(UI_COLOR_WHITE)
}

const toDisplayedDifferenceAmount = (value) =>
  value ? -Number(value) : 0

/* CARD */
const drawCard = (doc, x, y, w, h, title, value) => {
  const radius = 1.5

  doc.setFillColor(...COLORS.white)
  doc.roundedRect(x, y, w, h, radius, radius, 'F')

  doc.setDrawColor(...COLORS.lightBorder)
  doc.setLineWidth(0.35)
  doc.roundedRect(x, y, w, h, radius, radius, 'S')

  doc.setFontSize(7.5)
  doc.setFont(undefined, 'bold')
  doc.setTextColor(...COLORS.gray)
  doc.text(String(title), x + 3.5, y + 5.6)

  doc.setFontSize(13.5)
  doc.setFont(undefined, 'bold')
  doc.setTextColor(...COLORS.navy)
  doc.text(
    value === null || value === undefined ? '-' : String(value),
    x + 3.5,
    y + 11.8
  )
  doc.setFont(undefined, 'normal')
}

/* EXPORT PDF (RESUMEN) */
export const exportMetricsPDF = (
  summary = [],
  totals = {},
  columns,
  metrics,
  dateRange
) => {
  const doc = new jsPDF('landscape', 'mm', 'a4')
  const differenceAmounts = []
  const pageWidth = doc.internal.pageSize.getWidth()
  const tableStartX = 10
  const tableWidth = pageWidth - tableStartX * 2

  /*  HEADER  */
  let headerY = 12
  doc.setFontSize(8.5)
  doc.setTextColor(...COLORS.dark)

  const headerLabelX = 10
  const headerValueX = 30

  doc.text('REPORTE:', headerLabelX, headerY)
  doc.text('Fill Rate por cadena', headerValueX, headerY)

  headerY += 5
  doc.text('PERIODO:', headerLabelX, headerY)
  doc.text(String(dateRange || 'N/A'), headerValueX, headerY)

  headerY += 5
  doc.text('GENERADO:', headerLabelX, headerY)
  doc.text(new Date().toLocaleString(), headerValueX, headerY)

  /*  CARDS  */
  const cardY = headerY + 5
  const gap = 5
  const cardW = (tableWidth - gap * 3) / 4
  const cardH = 14.5

  drawCard(doc, tableStartX, cardY, cardW, cardH, 'ÓRDENES RECIBIDAS', tableValue(metrics?.received_orders))
  drawCard(doc, tableStartX + cardW + gap, cardY, cardW, cardH, 'PROCESADAS', tableValue(metrics?.processed_orders))
  drawCard(doc, tableStartX + (cardW + gap) * 2, cardY, cardW, cardH, 'TOTAL FACTURADO', moneyCell(metrics?.total_facturado))
  drawCard(doc, tableStartX + (cardW + gap) * 3, cardY, cardW, cardH, 'TICKET PROMEDIO', moneyCell(metrics?.ticket_promedio))

  /*  TABLE  */
  const bodyRows = summary.map(row => {
    const differenceAmount = toDisplayedDifferenceAmount(
      resolveFillRateDifference(row)
    )

    differenceAmounts.push(differenceAmount)

    return [
      tableValue(row.buyer),
      tableValue(row.purchase_order_counts),
      moneyCell(row.purchase_order_total),
      tableValue(row.order_counts),
      moneyCell(row.order_total),
      percentCell(row.fill_rate_order),
      differenceAmount === 0 ? '-' : '',
      moneyCell(row.salesorder_total),
      percentCell(row.fill_rate_sales),
      moneyCell(row.invoice_total),
      percentCell(row.fill_rate_invoice)
    ]
  })

  if (totals) {
    const totalDifferenceAmount = toDisplayedDifferenceAmount(totals.difference)

    differenceAmounts.push(totalDifferenceAmount)
    bodyRows.push([
      'TOTAL GENERAL',
      tableValue(totals.purchase_order_counts),
      moneyCell(totals.purchase_order_total),
      tableValue(totals.order_counts),
      moneyCell(totals.order_total),
      percentCell(totals.fill_rate_order),
      totalDifferenceAmount === 0 ? '-' : '',
      moneyCell(totals.salesorder_total),
      percentCell(totals.fill_rate_sales),
      moneyCell(totals.invoice_total),
      percentCell(totals.fill_rate_invoice)
    ])
  }

  autoTable(doc, {
    startY: cardY + cardH + 6,
    margin: { left: tableStartX, right: tableStartX },
    head: [[
      'CADENA',
      'OC CANT.',
      'OC MONTO',
      'PEDIDOS CANT.',
      'PEDIDOS MONTO',
      'FILL RATE PEDIDOS',
      'DIFERENCIA',
      'PEDIDOS ERP',
      'FILL RATE ERP',
      'FACTURA',
      'FILL RATE FACT.'
    ]],
    body: bodyRows,

    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontStyle: 'bold',
      fontSize: 6.5,
      minCellHeight: 8,
      lineColor: COLORS.lineBorder,
      lineWidth: 0.08,
      halign: 'center',
      valign: 'middle'
    },

    styles: {
      fontSize: 7,
      cellPadding: { top: 2.2, right: 2.2, bottom: 2.2, left: 2.2 },
      textColor: COLORS.dark,
      lineColor: COLORS.white,
      lineWidth: 0,
      overflow: 'linebreak',
      valign: 'middle'
    },

    columnStyles: {
      0: { halign: 'left', cellWidth: 34 },
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right' },
      4: { halign: 'right' },
      5: { halign: 'right', cellWidth: 19 },
      6: { halign: 'right' },
      7: { halign: 'right' },
      8: { halign: 'right', cellWidth: 17 },
      9: { halign: 'right' },
      10: { halign: 'right', cellWidth: 19 }
    },
    didParseCell: (data) => {
      if (data.section !== 'body') return

      const isTotalRow = data.row.index === bodyRows.length - 1 && Boolean(totals)

      data.cell.styles.fillColor =
        data.row.index % 2 === 0 ? COLORS.white : COLORS.rowAlt

      if (isTotalRow) {
        data.cell.styles.fontStyle = 'bold'
        data.cell.styles.fontSize = 8
        data.cell.styles.lineWidth = { top: 0.5 }
        data.cell.styles.lineColor = COLORS.totalBorder
      }
    },
    didDrawCell: (data) => {
      if (data.section !== 'body' || data.column.index !== 6) return

      const amount = differenceAmounts[data.row.index]
      if (!amount) return
      const isPositive = amount > 0

      doc.setFontSize(8)
      doc.setFont(undefined, 'bold')

      const label = moneyCell(amount)
      const chipPaddingX = 2.5
      const chipHeight = 6
      const chipWidth = doc.getTextWidth(label) + chipPaddingX * 2
      const chipX = data.cell.x + data.cell.width - chipWidth - 2
      const chipY = data.cell.y + (data.cell.height - chipHeight) / 2

      doc.setFillColor(...(isPositive ? COLORS.successBg : COLORS.dangerBg))
      doc.setDrawColor(...(isPositive ? COLORS.successBorder : COLORS.dangerBorder))
      doc.setLineWidth(0.2)
      doc.roundedRect(chipX, chipY, chipWidth, chipHeight, 1, 1, 'FD')

      doc.setTextColor(...(isPositive ? COLORS.success : COLORS.danger))
      doc.text(label, chipX + chipWidth / 2, chipY + 4.1, { align: 'center' })
      doc.setFont(undefined, 'normal')
      doc.setTextColor(...COLORS.dark)
    }
  })


  doc.save(buildFillRateExportFilename(cookie.load('business_name'), 'pdf'))
}
